import RiCheckLine from "~/icons/remix/generated/RiCheckLine"
import { Badge } from "~/components/Badge"
import { MarketingCtaLink } from "~/components/analytics/MarketingCtaLink"
import { siteConfig } from "~/lib/config/site"
import {
  ADS_FRAMEWORK_PRICE,
  ADS_SKILLS_PRICE,
  EMAIL_FRAMEWORK_PRICE,
  EMAIL_SKILLS_PRICE,
  formatCatalogPrice,
} from "~/lib/products/catalog"

/**
 * House CTA treatment — mirrors `Button`'s `primary` variant (Tremor Raw)
 * so the framework cards' CTAs read as siblings of the SaaS plan CTAs
 * above. `MarketingCtaLink` needs the classes inline (it renders its own
 * anchor), so this string is copied from `buttonVariants.primary` + base.
 */
const PRIMARY_CTA_CLASSES =
  "relative inline-flex w-full items-center justify-center rounded-lg border border-indigo-700 bg-indigo-800 px-3 py-2 text-center text-sm font-medium text-white shadow-sm transition-all duration-100 ease-in-out hover:bg-gradient-to-r from-rose-800 to-indigo-800 dark:border-indigo-800 dark:bg-indigo-600"

interface FrameworkPricingCard {
  name: string
  /** One-line positioning under the price, before the feature list. */
  description: string
  /** Compact stat chips — the card's visual anchor, mirroring the SaaS cards' capacity lines. */
  stats: readonly string[]
  /** Lead benefits — check-bulleted, same treatment as the SaaS plan features. */
  leadFeatures: readonly string[]
  /** Secondary details — compact, muted, under an "Also included" rule. */
  alsoIncluded: readonly string[]
  /** Catalog major-unit price string for the full framework SKU. */
  price: string
  href: string
  ctaLabel: string
  /** Skills-only cross-sell strip at the card foot. */
  skillsHref: string
  /** Catalog major-unit price string for the skills-only SKU. */
  skillsPrice: string
}

/**
 * Both framework SKUs, priced FROM the catalog constants (the same source
 * the merchant feed and every Offer JSON-LD node read) — never hand-typed,
 * so /pricing can't drift from checkout. Copy is grounded in the product
 * pages' own feature lists (`/frameworks/ads-framework`,
 * `/frameworks/email-framework`).
 */
const FRAMEWORK_CARDS: readonly FrameworkPricingCard[] = [
  {
    name: "Ads Framework",
    description:
      "Production-grade Remotion ad authoring: the agentic loop from brief to rendered MP4, on your machine.",
    stats: ["8 concepts", "12 formats", "11 skills + review agent"],
    leadFeatures: [
      "8 ad concepts × 12 platform formats — 96 render-ready combinations",
      "11 Claude Code skills + 1 review agent, from brief to MP4",
      "ElevenLabs voiceover + auto-burned captions",
      "Local-first render — no per-render fees",
    ],
    alsoIncluded: [
      "Platform-specific safe areas",
      "Brand-token-aware compositions",
      "Deterministic, snapshot-tested",
      "Lifetime engine updates",
    ],
    price: ADS_FRAMEWORK_PRICE,
    href: siteConfig.baseLinks.adsFramework,
    ctaLabel: "See the Ads Framework",
    skillsHref: siteConfig.baseLinks.adsSkills,
    skillsPrice: ADS_SKILLS_PRICE,
  },
  {
    name: "Email Framework",
    description:
      "Production-grade React Email lifecycle authoring: the agentic loop from brief to inbox-safe HTML + plaintext.",
    stats: ["6 templates", "4-client matrix", "8 skills + review agent"],
    leadFeatures: [
      "6 lifecycle templates — welcome, promo, launch, cart, newsletter, winback",
      "Renders across Apple Mail, Gmail, Outlook/MSO + dark mode",
      "8 Claude Code skills + 1 adversarial review agent",
      "Inlined, Outlook-safe HTML + a plaintext alternative",
    ],
    alsoIncluded: [
      "Local render — no per-send fees",
      "Brand-token-aware templates",
      "Deterministic, snapshot-tested",
      "Lifetime engine updates",
    ],
    price: EMAIL_FRAMEWORK_PRICE,
    href: siteConfig.baseLinks.emailFramework,
    ctaLabel: "See the Email Framework",
    skillsHref: siteConfig.baseLinks.emailSkills,
    skillsPrice: EMAIL_SKILLS_PRICE,
  },
]

/**
 * Companion pricing block on /pricing for the frameworks storefront —
 * both one-time SKUs (Ads + Email), presented with equal weight. Sits
 * BELOW the SaaS tier matrix — the frameworks are complements (one-time
 * developer/agency products) to the recurring SaaS, not competitors or
 * substitutes. Separating the two visually prevents the "is this the
 * same product?" confusion that a tab toggle would invite.
 *
 * Visual language deliberately mirrors the SaaS surfaces above: `Badge`
 * eyebrow, big `tabular-nums` price with a stacked small annotation,
 * indigo `RiCheckLine` bullets, and the `Button` primary CTA treatment —
 * with a hairline `border-t` announcing the zone change below the
 * comparison table instead of an abrupt centered heading.
 */
export function FrameworksPricingBlock(): JSX.Element {
  return (
    <section
      aria-labelledby="frameworks-pricing-title"
      className="mx-auto mb-4 mt-24 max-w-6xl border-t border-gray-200 px-4 pt-16 sm:px-6 lg:px-8 dark:border-gray-800"
    >
      <div className="mb-10 flex flex-col items-center text-center">
        <Badge>Frameworks</Badge>
        <h2
          id="frameworks-pricing-title"
          className="mt-4 text-balance text-3xl font-bold text-gray-900 md:text-4xl dark:text-gray-50"
        >
          Or build your own creative with code
        </h2>
        <p className="mt-3 max-w-2xl text-balance text-gray-600 dark:text-gray-400">
          The Ads and Email Frameworks are separate, one-time purchases for dev teams and
          agencies — no subscription. Lifetime updates included via your private npm registry
          token or a self-contained zip.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {FRAMEWORK_CARDS.map((card) => (
          <article
            key={card.name}
            aria-label={`${card.name} pricing`}
            className="flex flex-col rounded-2xl bg-white p-6 shadow-sm ring-1 ring-inset ring-gray-200 md:p-8 dark:bg-gray-950 dark:ring-gray-800"
          >
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-50">
              {card.name}
            </h3>
            <div className="mt-3 flex items-center gap-x-3">
              <span className="text-5xl font-semibold tabular-nums text-gray-900 dark:text-gray-50">
                {formatCatalogPrice(card.price)}
              </span>
              <div className="text-xs text-gray-600 dark:text-gray-400">
                one-time <br /> lifetime updates
              </div>
            </div>
            <p className="mt-5 text-sm leading-6 text-gray-600 dark:text-gray-400">
              {card.description}
            </p>
            <ul className="mt-4 flex flex-wrap gap-1.5" aria-label={`${card.name} at a glance`}>
              {card.stats.map((stat) => (
                <li
                  key={stat}
                  className="inline-flex items-center rounded-md bg-indigo-50 px-2 py-1 text-xs font-medium text-indigo-700 ring-1 ring-inset ring-indigo-600/10 dark:bg-indigo-400/10 dark:text-indigo-400 dark:ring-indigo-400/20"
                >
                  {stat}
                </li>
              ))}
            </ul>
            <div className="mt-6">
              <MarketingCtaLink
                href={card.href}
                placement="pricing_framework_block"
                linkText={card.ctaLabel}
                className={PRIMARY_CTA_CLASSES}
              >
                {card.ctaLabel}
              </MarketingCtaLink>
            </div>
            <ul role="list" className="mt-5 text-sm text-gray-700 dark:text-gray-400">
              {card.leadFeatures.map((item) => (
                <li key={item} className="flex items-start gap-x-3 py-1.5">
                  <RiCheckLine
                    className="mt-0.5 size-4 shrink-0 text-indigo-600 dark:text-indigo-400"
                    aria-hidden="true"
                  />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <div className="mt-4 border-t border-gray-100 pt-4 dark:border-gray-800">
              {/* dark:text-gray-400, NOT gray-500: the site defaults to dark
                  theme, so Lighthouse's error-level color-contrast audit
                  measures this label on the dark card — gray-500 on
                  dark:bg-gray-950 is 4.16:1 (< 4.5:1 AA); gray-400 is 7.93:1.
                  Light stays gray-500 on the white card (4.83:1). */}
              <p className="text-[11px] font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                Also included
              </p>
              <ul role="list" className="mt-2 grid grid-cols-1 gap-x-4 gap-y-1.5 sm:grid-cols-2">
                {card.alsoIncluded.map((item) => (
                  <li
                    key={item}
                    className="flex items-center gap-x-2 text-xs text-gray-600 dark:text-gray-400"
                  >
                    <span
                      aria-hidden
                      className="inline-block size-1 shrink-0 rounded-full bg-gray-400 dark:bg-gray-600"
                    />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="mt-auto pt-6">
              <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 rounded-lg bg-gray-50 px-3.5 py-2.5 ring-1 ring-inset ring-gray-200/70 dark:bg-gray-900 dark:ring-gray-800">
                <p className="text-xs text-gray-600 dark:text-gray-400">
                  Not a coder? Skills-only edition, no engine.
                </p>
                <MarketingCtaLink
                  href={card.skillsHref}
                  placement="pricing_framework_block"
                  linkText={`${card.name} skills-only — ${formatCatalogPrice(card.skillsPrice)}`}
                  className="text-xs font-semibold text-indigo-600 hover:underline dark:text-indigo-400"
                >
                  {formatCatalogPrice(card.skillsPrice)} one-time →
                </MarketingCtaLink>
              </div>
            </div>
          </article>
        ))}
      </div>

      {/* text-gray-600, NOT gray-500: this line sits directly on the page
          body's bg-slate-100/90 (effective #f2f6fa), where gray-500 is
          4.45:1 — just under the 4.5:1 AA floor. gray-600 is 6.96:1. */}
      <p className="mt-8 text-center text-sm text-gray-600 dark:text-gray-400">
        Lifetime updates on every SKU · No subscription ·{" "}
        <MarketingCtaLink
          href={siteConfig.baseLinks.frameworks}
          placement="pricing_framework_block"
          linkText="Compare both frameworks →"
          className="font-semibold text-indigo-600 hover:underline dark:text-indigo-400"
        >
          Compare both frameworks →
        </MarketingCtaLink>
      </p>
    </section>
  )
}
